import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { useProductsStore } from '../store/productsStore';
import { useAdminStore } from '../admin/store/adminStore';
import { useAuthStore } from '../store/authStore';
import { formatPrice } from '../data/products';
import { Ticket, X, CreditCard, Loader2, Check, AlertCircle, User, Mail, Phone, MapPin, Building, FileText } from 'lucide-react';
import { getPaytrIframeUrl } from '../services/paytr';
import apiService from '../services/api';




// Validate Turkish phone number
const validateTurkishPhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  // Turkish mobile numbers start with 5 and have 10 digits
  return cleaned.length === 10 && cleaned.startsWith('5');
};

// Validate email
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Sanitize input to prevent XSS
const sanitizeInput = (input: string): string => {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Generate CSRF token (simplified for client-side)
const generateCSRFToken = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const PaymentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart, generateOrderNumber } = useStore();
  const { addOrder, validateCoupon } = useAdminStore();
  const { shippingCost: SHIPPING_FEE, freeShippingMin: FREE_SHIPPING_THRESHOLD } = useProductsStore((state) => state.shipping);
  const { user, isAuthenticated } = useAuthStore();
  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number } | null>(null);
  const [couponError, setCouponError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [showPaytrIframe, setShowPaytrIframe] = useState(false);
  const [paytrToken, setPaytrToken] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [csrfToken] = useState(generateCSRFToken());

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    city: user?.city || '',
    district: user?.district || '',
    address: user?.address || '',
    apartment: '',
    zipCode: user?.postalCode || '',
    note: '',
    invoiceType: 'individual',
    companyName: '',
    taxNumber: '',
    taxOffice: '',
  });

  const subtotal = getCartTotal();
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const couponDiscount = appliedCoupon ? appliedCoupon.discount : 0;
  const total = Math.max(0, subtotal + shipping - couponDiscount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    const result = await validateCoupon(couponCode.trim(), subtotal);
    if (result.valid) {
      setAppliedCoupon({ code: couponCode.trim().toUpperCase(), discount: result.discount });
      setCouponError('');
    } else {
      setCouponError(result.error || 'Geçersiz kupon');
      setAppliedCoupon(null);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Phone formatting: auto-format as XXX XXX XX XX (Turkish mobile format)
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '').substring(0, 10);
      let formatted = cleaned;
      if (cleaned.length > 3) formatted = cleaned.substring(0, 3) + ' ' + cleaned.substring(3);
      if (cleaned.length > 6) formatted = cleaned.substring(0, 3) + ' ' + cleaned.substring(3, 6) + ' ' + cleaned.substring(6);
      if (cleaned.length > 8) formatted = cleaned.substring(0, 3) + ' ' + cleaned.substring(3, 6) + ' ' + cleaned.substring(6, 8) + ' ' + cleaned.substring(8);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }

    // Sanitize text inputs (except for card number and phone which are already formatted)
    if (name !== 'phone') {
      const sanitized = sanitizeInput(value);
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validateStep1 = (): boolean => {
    const errors: string[] = [];
    const newFieldErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      errors.push('Ad alanı zorunludur');
      newFieldErrors.firstName = 'Ad gereklidir';
    }
    if (!formData.lastName.trim()) {
      errors.push('Soyad alanı zorunludur');
      newFieldErrors.lastName = 'Soyad gereklidir';
    }
    if (!formData.email.trim()) {
      errors.push('E-posta alanı zorunludur');
      newFieldErrors.email = 'E-posta gereklidir';
    } else if (!validateEmail(formData.email)) {
      errors.push('Geçerli bir e-posta adresi giriniz');
      newFieldErrors.email = 'Geçersiz e-posta formatı';
    }
    if (!formData.phone.trim()) {
      errors.push('Telefon alanı zorunludur');
      newFieldErrors.phone = 'Telefon gereklidir';
    } else if (!validateTurkishPhone(formData.phone)) {
      errors.push('Geçerli bir telefon numarası giriniz (5XX XXX XX XX)');
      newFieldErrors.phone = 'Geçersiz telefon formatı';
    }
    if (!formData.city.trim()) {
      errors.push('İl alanı zorunludur');
      newFieldErrors.city = 'İl gereklidir';
    }
    if (!formData.district.trim()) {
      errors.push('İlçe alanı zorunludur');
      newFieldErrors.district = 'İlçe gereklidir';
    }
    if (!formData.address.trim()) {
      errors.push('Adres alanı zorunludur');
      newFieldErrors.address = 'Adres gereklidir';
    }
    
    setValidationErrors(errors);
    setFieldErrors(newFieldErrors);
    return errors.length === 0;
  };

  const validateStep2 = (): boolean => {
    const errors: string[] = [];
    const newFieldErrors: Record<string, string> = {};

    if (!acceptTerms) {
      errors.push('Şartları ve koşulları kabul etmelisiniz');
      newFieldErrors.terms = 'Şartlar zorunludur';
    }
    
    setValidationErrors(errors);
    setFieldErrors(newFieldErrors);
    return errors.length === 0;
  };

  const handleGoToPayment = () => {
    if (validateStep1()) {
      setStep(2);
      setValidationErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }
    
    setIsProcessing(true);
    setPaymentError('');

    try {
      const orderNum = generateOrderNumber();
      setOrderNumber(orderNum);

      // Create payment via backend API (phone formatted as 90XXXXXXXXXX for PayTr)
      const cleanPhone = formData.phone.replace(/\D/g, '');
      const paytrPhone = '90' + cleanPhone;
      const paymentResult = await apiService.createPayment({
        orderId: orderNum,
        amount: total,
        currency: 'TL',
        customerName: `${formData.firstName} ${formData.lastName}`,
        customerEmail: formData.email,
        customerPhone: paytrPhone,
        shippingAddress: `${formData.address}${formData.apartment ? ' ' + formData.apartment : ''}, ${formData.district}, ${formData.city}`,
        billingAddress: `${formData.address}${formData.apartment ? ' ' + formData.apartment : ''}, ${formData.district}, ${formData.city}`,
        city: formData.city,
        district: formData.district,
        postalCode: formData.zipCode,
        items: cart.map(item => ({
          productId: item.product.id,
          name: item.product.name,
          quantity: item.quantity,
          unitPrice: item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice,
        })),
        notes: formData.note,
        couponCode: appliedCoupon?.code,
        couponDiscount: appliedCoupon?.discount || 0,
        csrfToken: csrfToken,
      });

      if (paymentResult.success && paymentResult.iframe_url) {
        // Add order to admin store
        addOrder({
          orderNumber: orderNum,
          customerName: `${formData.firstName} ${formData.lastName}`,
          customerEmail: formData.email,
          customerPhone: cleanPhone,
          shippingAddress: `${formData.address}${formData.apartment ? ' ' + formData.apartment : ''}`,
          city: formData.city,
          district: formData.district,
          postalCode: formData.zipCode,
          items: cart.map(item => ({ product: item.product, quantity: item.quantity })),
          totalAmount: total,
          shippingCost: shipping,
          status: 'pending',
          paymentMethod: 'credit_card',
          paymentStatus: 'pending',
          notes: formData.note,
          userId: user?.id || null,
        });

        // Show Paytr iframe (gerçek Paytr entegrasyonu düz bir <iframe src> ile çalışır,
        // özel bir JS kütüphanesi/script yüklemeye gerek yoktur)
        setPaytrToken(paymentResult.token || '');
        setShowPaytrIframe(true);
      } else {
        setPaymentError(paymentResult.error || 'Ödeme başlatılamadı');
        setIsProcessing(false);
      }
    } catch (error) {
      setPaymentError('Bir hata oluştu. Lütfen tekrar deneyin.');
      setIsProcessing(false);
    }
  };

  // Paytr iframe modal
  if (showPaytrIframe) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-lg mx-4 w-full">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">💳 Güvenli Ödeme</h2>
            <button
              onClick={() => {
                setShowPaytrIframe(false);
                setIsProcessing(false);
              }}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-600">Sipariş No: <span className="font-bold">{orderNumber}</span></p>
            <p className="text-lg font-bold text-[#E31E24]">Toplam: {formatPrice(total)} ₺</p>
          </div>

          <div id="paytr-iframe-container" className="min-h-[400px] bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">
            {paytrToken ? (
              <iframe
                src={getPaytrIframeUrl(paytrToken)}
                title="Paytr Güvenli Ödeme"
                frameBorder="0"
                scrolling="auto"
                style={{ width: '100%', height: '620px', border: 'none' }}
              />
            ) : (
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#E31E24] mx-auto mb-2" />
                <p className="text-sm text-gray-500">Ödeme formu yükleniyor...</p>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-gray-400 justify-center">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>256-bit SSL ile güvenli ödeme. Paytr altyapısı kullanılmaktadır.</span>
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-xl p-12 text-center max-w-md mx-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Siparişiniz Alındı!</h2>
          <p className="text-gray-500 mb-4">Sipariş numaranız cep telefonu ve e-posta adresinize gönderilecektir.</p>
          <div className="bg-gray-50 border-2 border-dashed border-[#E31E24] rounded-xl px-6 py-4 mb-6">
            <p className="text-xs text-gray-500 mb-1">Sipariş Numaranız</p>
            <p className="text-xl font-black text-[#E31E24] tracking-wider">{orderNumber}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#E31E24] text-white font-bold rounded-xl hover:bg-[#c41a1f] transition-colors"
          >
            Alışverişe Devam Et
          </button>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🛒</div>
          <p className="text-gray-500 text-lg mb-4">Sepetiniz boş</p>
          <button
            onClick={() => navigate('/magaza')}
            className="px-6 py-3 bg-[#E31E24] text-white font-bold rounded-xl hover:bg-[#c41a1f] transition-colors"
          >
            Alışverişe Başla
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a2e] text-white py-8 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-4xl font-black">💳 Sipariş Tamamla</h1>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">Siparişinizi güvenle tamamlayın</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 animate-fade-in">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#E31E24] text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
            <span className={`text-sm font-semibold ${step >= 1 ? 'text-[#E31E24]' : 'text-gray-400'}`}>Bilgiler</span>
          </div>
          <div className="w-12 h-px bg-gray-300 mx-2" />
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#E31E24] text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
            <span className={`text-sm font-semibold ${step >= 2 ? 'text-[#E31E24]' : 'text-gray-400'}`}>Ödeme</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left - Form */}
            <div className="lg:col-span-2 space-y-6">
              {step === 1 && (
                <div className="animate-fade-in">
                  {/* Customer Info */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm mb-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">👤 Kişisel Bilgiler</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Ad *</label>
                        <div className="relative">
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm ${fieldErrors.firstName ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.firstName && <p className="text-xs text-red-500 mt-1">{fieldErrors.firstName}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Soyad *</label>
                        <div className="relative">
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm ${fieldErrors.lastName ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.lastName && <p className="text-xs text-red-500 mt-1">{fieldErrors.lastName}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">E-posta *</label>
                        <div className="relative">
                          <input type="text" name="email" inputMode="email" value={formData.email} onChange={handleChange} required className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm ${fieldErrors.email ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Telefon *</label>
                        <div className="relative">
                          <input type="text" name="phone" inputMode="numeric" value={formData.phone} onChange={handleChange} required placeholder="5XX XXX XX XX" className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm ${fieldErrors.phone ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.phone && <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">📍 Teslimat Adresi</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">İl *</label>
                        <div className="relative">
                          <input type="text" name="city" value={formData.city} onChange={handleChange} required className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm ${fieldErrors.city ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.city && <p className="text-xs text-red-500 mt-1">{fieldErrors.city}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">İlçe *</label>
                        <div className="relative">
                          <input type="text" name="district" value={formData.district} onChange={handleChange} required className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm ${fieldErrors.district ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.district && <p className="text-xs text-red-500 mt-1">{fieldErrors.district}</p>}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Adres *</label>
                        <div className="relative">
                          <textarea name="address" value={formData.address} onChange={handleChange} required rows={2} className={`w-full px-3 py-3 sm:py-2.5 border-2 rounded-xl focus:outline-none text-sm resize-none ${fieldErrors.address ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-[#E31E24]'}`} />
                          {fieldErrors.address && <p className="text-xs text-red-500 mt-1">{fieldErrors.address}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Bina / Daire No</label>
                        <input type="text" name="apartment" value={formData.apartment} onChange={handleChange} className="w-full px-3 py-3 sm:py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Posta Kodu</label>
                        <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} className="w-full px-3 py-3 sm:py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm" />
                      </div>
                    </div>
                    {isAuthenticated && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-4 h-4 text-[#E31E24] border-gray-300 rounded focus:ring-[#E31E24]" />
                          <span className="text-sm text-gray-600">Bu adresi gelecek siparişlerim için kaydet</span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Invoice Type */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">🧾 Fatura Türü</h2>
                    <div className="flex gap-4 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="invoiceType" value="individual" checked={formData.invoiceType === 'individual'} onChange={handleChange} className="text-[#E31E24]" />
                        <span className="text-sm font-medium">Bireysel</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="invoiceType" value="corporate" checked={formData.invoiceType === 'corporate'} onChange={handleChange} className="text-[#E31E24]" />
                        <span className="text-sm font-medium">Kurumsal</span>
                      </label>
                    </div>
                    {formData.invoiceType === 'corporate' && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Şirket Adı</label>
                          <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="w-full px-3 py-3 sm:py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Vergi Numarası</label>
                          <input type="text" name="taxNumber" value={formData.taxNumber} onChange={handleChange} className="w-full px-3 py-3 sm:py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Vergi Dairesi</label>
                          <input type="text" name="taxOffice" value={formData.taxOffice} onChange={handleChange} className="w-full px-3 py-3 sm:py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm" />
                        </div>
                      </div>
                    )}
                  </div>

                  {validationErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-sm font-semibold text-red-700">Lütfen aşağıdaki alanları doldurunuz:</p>
                      </div>
                      <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5 ml-7">
                        {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleGoToPayment}
                    className="w-full py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#E31E24]/30 transition-all duration-300 uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                  >
                    <span>Ödemeye Geç</span>
                    <span>→</span>
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="animate-fade-in">
                  {/* Order Review */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm mb-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">📋 Sipariş Özeti</h2>

                    {paymentError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600">{paymentError}</p>
                      </div>
                    )}

                    <div className="mb-5">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Teslimat Adresi</h3>
                      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700">
                        <p className="font-semibold">{formData.firstName} {formData.lastName}</p>
                        <p>{formData.address}{formData.apartment ? `, ${formData.apartment}` : ''}</p>
                        <p>{formData.district} / {formData.city} {formData.zipCode}</p>
                        <p className="text-gray-500 mt-1">{formData.phone} · {formData.email}</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Ürünler ({cart.length})</h3>
                      <div className="space-y-2">
                        {cart.map((item) => (
                          <div key={item.product.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-b-0">
                            <span className="text-gray-700">{item.product.name} <span className="text-gray-400">× {item.quantity}</span></span>
                            <span className="font-semibold text-gray-800">
                              {formatPrice((item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice) * item.quantity)} ₺
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span>Ödemenizi bir sonraki adımda Paytr'ın güvenli 256-bit SSL korumalı ödeme ekranında yapacaksınız. Kart bilgileriniz sitemiz üzerinden geçmez.</span>
                    </div>
                  </div>

                  {/* Note */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">📝 Sipariş Notu</h2>
                    <textarea name="note" value={formData.note} onChange={handleChange} rows={2} placeholder="Siparişinizle ilgili eklemek istediğiniz bir not varsa yazabilirsiniz." className="w-full px-3 py-3 sm:py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm resize-none" />
                  </div>

                  {/* Terms and Conditions */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => {
                          setAcceptTerms(e.target.checked);
                          if (e.target.checked) {
                            setFieldErrors(prev => ({ ...prev, terms: '' }));
                            setValidationErrors(prev => prev.filter(err => !err.includes('Şartları')));
                          }
                        }}
                        className="mt-1 w-5 h-5 text-[#E31E24] border-gray-300 rounded focus:ring-[#E31E24]"
                      />
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">Şartları ve koşulları</span> okudum, kabul ediyorum. 
                        <span className="text-[#E31E24] ml-1">*</span>
                        {fieldErrors.terms && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.terms}</p>
                        )}
                      </div>
                    </label>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-colors text-sm"
                    >
                      ← Geri
                    </button>
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className={`flex-1 py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#E31E24]/30 transition-all duration-300 uppercase tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isProcessing ? 'animate-pulse-glow' : ''}`}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Ödeme Yapılıyor...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          <span>🛒 Siparişi Tamamla</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm sticky top-24">
                <h2 className="text-lg font-bold text-gray-800 mb-4">📋 Sipariş Özeti</h2>
                <div className="space-y-3 mb-4">
                  {cart.map((item) => {
                    const price = item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice;
                    return (
                      <div key={item.product.id} className="flex gap-3 items-center">
                        <img src={item.product.images[0]} alt={item.product.name} className="w-12 h-12 object-contain bg-gray-50 rounded-lg" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">{item.product.name}</p>
                          <p className="text-xs text-gray-400">{item.quantity} adet</p>
                        </div>
                        <span className="text-xs font-bold text-gray-800">{formatPrice(price * item.quantity)} ₺</span>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Ara Toplam</span>
                    <span className="font-semibold">{formatPrice(subtotal)} ₺</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Kargo</span>
                    <span className={`font-semibold ${shipping === 0 ? 'text-green-600' : ''}`}>
                      {shipping === 0 ? 'Ücretsiz' : `${formatPrice(shipping)} ₺`}
                    </span>
                  </div>
                  {shipping > 0 && (
                    <p className="text-xs text-[#E31E24]">🎉 {FREE_SHIPPING_THRESHOLD} TL ve üzeri alışverişlerde ücretsiz kargo!</p>
                  )}

                  {/* Coupon Section */}
                  <div className="border-t border-gray-100 pt-3">
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-50 rounded-xl px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Ticket className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-bold text-green-700">{appliedCoupon.code}</span>
                          <span className="text-xs text-green-600">-{formatPrice(appliedCoupon.discount)}₺</span>
                        </div>
                        <button
                          onClick={handleRemoveCoupon}
                          className="p-1 rounded-lg hover:bg-green-100 text-green-500"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                            placeholder="Kupon kodu"
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-xs font-mono tracking-wider"
                          />
                          <button
                            onClick={handleApplyCoupon}
                            className="px-3 py-2 bg-gray-900 text-white rounded-xl text-xs font-semibold hover:bg-gray-800 transition-colors"
                          >
                            Uygula
                          </button>
                        </div>
                        {couponError && (
                          <p className="text-xs text-red-500 mt-1">{couponError}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Kupon İndirimi</span>
                      <span className="font-semibold text-green-600">-{formatPrice(couponDiscount)} ₺</span>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-2 flex justify-between">
                    <span className="text-lg font-bold text-gray-800">Toplam</span>
                    <span className="text-lg font-black text-[#E31E24]">{formatPrice(total)} ₺</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentPage;
